import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Copy, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../../../../lib/supabase';
import { generatePreviewToken, buildPreviewUrl } from '../../../../utils/previewToken';

interface PreviewLinkSectionProps {
  propertyId?: string;
}

/**
 * Gestione del link privato da girare a un cliente.
 *
 * Scrive direttamente sul database e non passa dal form: il token deve essere
 * legato a un immobile che esiste già, altrimenti il link punterebbe a un id
 * mai salvato. Per lo stesso motivo la sezione non compare sugli immobili nuovi.
 */
const PreviewLinkSection: React.FC<PreviewLinkSectionProps> = ({ propertyId }) => {
  const [token, setToken] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const loadToken = useCallback(async () => {
    if (!propertyId) {
      setLoading(false);
      return;
    }

    // maybeSingle: l'assenza di link è la normalità, non un errore.
    const { data, error } = await supabase
      .from('property_preview_links')
      .select('token, created_at')
      .eq('property_id', propertyId)
      .maybeSingle();

    if (error) {
      console.error('Errore nel caricamento del link privato:', error);
    } else {
      setToken(data?.token ?? null);
      setCreatedAt(data?.created_at ?? null);
    }

    setLoading(false);
  }, [propertyId]);

  useEffect(() => {
    loadToken();
  }, [loadToken]);

  // Revocare è cancellare la riga: il vecchio indirizzo smette di rispondere
  // subito, anche a chi ce l'ha già aperto e ricarica la pagina.
  const saveToken = async (newToken: string | null) => {
    if (!propertyId) return;
    setWorking(true);

    const createdAtIso = new Date().toISOString();

    const { error } = newToken
      ? await supabase
          .from('property_preview_links')
          .upsert(
            { property_id: propertyId, token: newToken, created_at: createdAtIso },
            { onConflict: 'property_id' }
          )
      : await supabase
          .from('property_preview_links')
          .delete()
          .eq('property_id', propertyId);

    setWorking(false);

    if (error) {
      console.error('Errore sul link privato:', error);
      toast.error('Non sono riuscito a salvare il link privato');
      return;
    }

    setToken(newToken);
    setCreatedAt(newToken ? createdAtIso : null);
  };

  const handleGenerate = async () => {
    await saveToken(generatePreviewToken());
    toast.success('Link privato generato');
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Il link attuale smetterà di funzionare, anche se lo hai già mandato a qualcuno. Procedo?')) return;
    await saveToken(generatePreviewToken());
    toast.success('Nuovo link generato, il precedente non è più valido');
  };

  const handleRevoke = async () => {
    if (!window.confirm('Chi ha il link non vedrà più la scheda. Procedo?')) return;
    await saveToken(null);
    toast.success('Link revocato');
  };

  const handleCopy = async () => {
    if (!token) return;
    const url = buildPreviewUrl(token);

    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiato');
    } catch {
      // Contesti senza clipboard API (http, permessi negati): lasciamo l'URL
      // visibile nel campo così resta copiabile a mano.
      window.prompt('Copia il link:', url);
    }
  };

  if (!propertyId) {
    return (
      <div className="pt-4 border-t border-gray-200">
        <h4 className="font-medium text-gray-700 mb-2">Link privato</h4>
        <p className="text-sm text-gray-500">
          Salva l'immobile per poter generare il link da girare a un cliente.
        </p>
      </div>
    );
  }

  const url = token ? buildPreviewUrl(token) : '';

  return (
    <div className="pt-4 border-t border-gray-200">
      <h4 className="font-medium text-gray-700 mb-2">Link privato</h4>
      <p className="text-sm text-gray-500 mb-4">
        Un indirizzo segreto che mostra la scheda anche se l'immobile non è pubblicato.
        Non è indicizzato dai motori di ricerca e non ha navigazione verso il sito.
        Chi lo riceve può girarlo ad altri: revocalo quando non ti serve più.
      </p>

      {loading ? (
        <div className="h-10 bg-gray-100 rounded animate-pulse" />
      ) : token ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={url}
              onFocus={event => event.target.select()}
              className="flex-grow min-w-0 border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-700"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="flex-shrink-0 bg-primary hover:bg-primary-light text-white rounded-md px-3 py-2"
              title="Copia il link"
            >
              <Copy className="h-4 w-4" />
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-md px-3 py-2"
              title="Apri il link"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          {createdAt && (
            <p className="text-xs text-gray-400">
              Generato il {new Date(createdAt).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={working}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Rigenera
            </button>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={working}
              className="text-sm text-red-600 hover:text-red-800 flex items-center disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Revoca
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={working}
          className="bg-secondary hover:bg-secondary-light text-white rounded-lg px-4 py-2.5 flex items-center disabled:opacity-50"
        >
          <Link2 className="h-4 w-4 mr-2" />
          Genera link privato
        </button>
      )}
    </div>
  );
};

export default PreviewLinkSection;
